import { axiosInstance } from '@/lib/axios'
import type { Delegation } from '@/app/types/creator'

export type ApiDelegation = {
  tripId?: number | string
  TripId?: number | string
  delegationId?: string
  DelegationId?: string
  vehicleReg?: string
  VehicleReg?: string
  start?: string
  Start?: string
  end?: string
  End?: string
  duration?: string
  Duration?: string
  accommodation?: number
  Accommodation?: number
  costs?: number
  Costs?: number
  advances?: number
  Advances?: number
  allowances?: number
  Allowances?: number
  destinationCountry?: string
  DestinationCountry?: string
}

export type ApiDriverDelegation = {
  driverId?: number | string
  DriverId?: number | string
  name?: string
  Name?: string
  summary?: {
    totalTime?: string
    TotalTime?: string
    totalOvertime?: string
    TotalOvertime?: string
    totalAccommodation?: number
    TotalAccommodation?: number
    totalAllowances?: number
    TotalAllowances?: number
  }
  Summary?: {
    totalTime?: string
    TotalTime?: string
    totalOvertime?: string
    TotalOvertime?: string
    totalAccommodation?: number
    TotalAccommodation?: number
    totalAllowances?: number
    TotalAllowances?: number
  }
  delegations?: ApiDelegation[]
  Delegations?: ApiDelegation[]
}

export type CreatorDelegationsTableSlice = {
  delegations: Delegation[]
  summary: {
    totalTime: string
    totalOvertime: string
    totalAccommodation: number
    totalAllowances: number
  }
}

export const mapApiDelegationToDelegation = (
  delegation: ApiDelegation,
): Delegation => ({
  tripId: String(delegation.tripId ?? delegation.TripId ?? ''),
  delegationId: String(delegation.delegationId ?? delegation.DelegationId ?? ''),
  vehicleReg: String(delegation.vehicleReg ?? delegation.VehicleReg ?? ''),
  start: String(delegation.start ?? delegation.Start ?? ''),
  end: String(delegation.end ?? delegation.End ?? ''),
  duration: String(delegation.duration ?? delegation.Duration ?? ''),
  accommodation: Number(delegation.accommodation ?? delegation.Accommodation ?? 0),
  costs: Number(delegation.costs ?? delegation.Costs ?? 0),
  advances: Number(delegation.advances ?? delegation.Advances ?? 0),
  allowances: Number(delegation.allowances ?? delegation.Allowances ?? 0),
  destinationCountry: String(
    delegation.destinationCountry ?? delegation.DestinationCountry ?? '',
  ),
})

export const parseDelegationsApiDrivers = (data: unknown): ApiDriverDelegation[] => {
  if (Array.isArray(data)) {
    return data as ApiDriverDelegation[]
  }
  const record = data as Record<string, unknown> | null | undefined
  if (Array.isArray(record?.drivers)) {
    return record.drivers as ApiDriverDelegation[]
  }
  if (Array.isArray(record?.Drivers)) {
    return record.Drivers as ApiDriverDelegation[]
  }
  return []
}

export const mapApiDriverDelegationToTableSlice = (
  apiDriver: ApiDriverDelegation | undefined,
): CreatorDelegationsTableSlice => {
  const summaryRaw = apiDriver?.summary ?? apiDriver?.Summary ?? {}
  const apiDelegations =
    apiDriver?.delegations ?? apiDriver?.Delegations ?? []
  const mappedDelegations = apiDelegations.map(mapApiDelegationToDelegation)

  return {
    delegations: mappedDelegations,
    summary: {
      totalTime: String(summaryRaw.totalTime ?? summaryRaw.TotalTime ?? '0m'),
      totalOvertime: String(
        summaryRaw.totalOvertime ?? summaryRaw.TotalOvertime ?? '0',
      ),
      totalAccommodation: Number(
        summaryRaw.totalAccommodation ??
          summaryRaw.TotalAccommodation ??
          mappedDelegations.reduce((acc, d) => acc + d.accommodation, 0),
      ),
      totalAllowances: Number(
        summaryRaw.totalAllowances ??
          summaryRaw.TotalAllowances ??
          mappedDelegations.reduce((acc, d) => acc + d.allowances, 0),
      ),
    },
  }
}

export const findApiDriverEntry = (
  apiDrivers: ApiDriverDelegation[],
  driverId: string,
): ApiDriverDelegation | undefined =>
  apiDrivers.find(
    (entry) =>
      String(entry.driverId ?? entry.DriverId ?? '') === String(driverId),
  )

export const fetchCreatorDelegationsFromApi = async ({
  companyId,
  startTime,
  endTime,
  driverIds,
}: {
  companyId: string
  startTime: string
  endTime: string
  driverIds: number[]
}): Promise<ApiDriverDelegation[]> => {
  const { data } = await axiosInstance.post(
    `/api/${companyId}/driver/delegations/get`,
    {
      startTime,
      endTime,
      driverIds,
    },
  )
  return parseDelegationsApiDrivers(data)
}

export const driverHasDelegationsInPeriod = (
  delegations: Delegation[] | undefined,
): boolean => (delegations?.length ?? 0) > 0
